import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Package, Truck, Search, ExternalLink, HandHeart } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");

const Step = ({ done, icon: Icon, title, sub, testId }) => (
  <li className={`flex gap-4 items-start ${done ? "" : "opacity-50"}`} data-testid={testId}>
    <span className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center ${done ? "bg-[#C8D0B7] text-[#4A3B32]" : "bg-[#EAECE1] text-[#4A3B32]/60"}`}>
      <Icon className="w-5 h-5" />
    </span>
    <div>
      <p className="font-cursive text-2xl text-[#78353A] leading-tight">{title}</p>
      <p className="text-sm text-[#4A3B32]/75">{sub}</p>
    </div>
  </li>
);

const OrderResult = ({ order }) => {
  const shipped = order.status === "shipped";
  const delivered = order.status === "delivered";
  return (
    <div className="mt-8 rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-6 md:p-8" data-testid="tracking-result">
      <p className="font-cursive text-2xl text-[#78353A]/70">Commande n°</p>
      <h2 className="font-cursive text-5xl text-[#78353A] leading-none" data-testid="tracking-order-number">{order.order_number}</h2>
      <p className="mt-1 text-sm text-[#4A3B32]/70">Passée le {fmtDate(order.created_at)}{order.destination ? ` · Destination : ${order.destination}` : ""}</p>

      <ol className="mt-7 space-y-5">
        <Step done icon={Package} title="Commande reçue" sub="Milo a tout noté dans son carnet, ton colis est préparé avec soin." testId="tracking-step-paid" />
        {delivered ? (
          <Step done icon={HandHeart} title="Remise en mains propres" sub={`Ta commande t'a été remise directement le ${fmtDate(order.delivered_at)}. Bonne écriture ✿`} testId="tracking-step-delivered" />
        ) : (
          <Step
            done={shipped}
            icon={Truck}
            title={shipped ? "Colis en route !" : "Expédition à venir"}
            sub={shipped ? `Expédié le ${fmtDate(order.shipped_at)}${order.carrier ? ` avec ${order.carrier}` : ""}.` : "Tu recevras un e-mail avec le lien de suivi dès le départ du colis."}
            testId="tracking-step-shipped"
          />
        )}
      </ol>

      {shipped && order.tracking_number && (
        <div className="mt-7 rounded-3xl bg-[#EEF1E4] border-2 border-dashed border-[#C8D0B7] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="tracking-info">
          <div>
            <p className="text-xs uppercase tracking-wide text-[#4A3B32]/60">Numéro de suivi</p>
            <p className="font-cursive text-3xl text-[#78353A] leading-none" data-testid="tracking-number">{order.tracking_number}</p>
          </div>
          {order.tracking_url && (
            <a href={order.tracking_url} target="_blank" rel="noreferrer" data-testid="tracking-link">
              <FlowerButton variant="sage" className="!py-2 !px-5 !text-lg">Suivre mon colis <ExternalLink className="inline w-4 h-4 ml-1" /></FlowerButton>
            </a>
          )}
        </div>
      )}

      {order.items?.length > 0 && (
        <div className="mt-7">
          <p className="font-cursive text-2xl text-[#78353A] mb-2">Dans ton colis</p>
          <ul className="text-sm text-[#4A3B32]/85 space-y-1" data-testid="tracking-items">
            {order.items.map((i, k) => <li key={k}>{i.quantity} × {i.name}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function Suivi() {
  const [params, setParams] = useSearchParams();
  const [number, setNumber] = useState(params.get("commande") || "");
  const [state, setState] = useState({ loading: false, order: null, error: null });

  const search = async (n) => {
    const clean = (n || "").trim();
    if (!clean) return;
    setState({ loading: true, order: null, error: null });
    try {
      const { data } = await axios.get(`${API}/orders/track/${encodeURIComponent(clean)}`);
      setState({ loading: false, order: data, error: null });
      setParams({ commande: data.order_number }, { replace: true });
    } catch (e) {
      setState({ loading: false, order: null, error: e.response?.status === 404 ? "notfound" : "error" });
    }
  };

  useEffect(() => { if (params.get("commande")) search(params.get("commande")); }, []); // eslint-disable-line

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14" data-testid="suivi-page">
      <p className="font-cursive text-2xl text-[#78353A]/70">Suivi de commande</p>
      <h1 className="font-cursive text-6xl md:text-7xl text-[#78353A] leading-none">Où est mon colis&nbsp;?</h1>
      <p className="mt-4 text-[#4A3B32]/85 leading-relaxed max-w-xl">
        Entre ton numéro de commande (il commence par <span className="font-semibold">MM-</span>, tu le trouves dans ton e-mail de confirmation) pour savoir où en est ton colis.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); search(number); }} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="tracking-form">
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="MM-XXXXXXXX"
          aria-label="Numéro de commande"
          className="flex-1 px-5 py-4 rounded-full bg-[#FAF9F6] border-2 border-dashed border-[#D1D5CB] text-[#4A3B32] placeholder-[#4A3B32]/40 focus:outline-none focus:border-[#78353A] transition-colors font-medium uppercase"
          data-testid="tracking-input"
        />
        <FlowerButton type="submit" disabled={state.loading} data-testid="tracking-submit" className="!py-3 !px-6 !text-lg">
          {state.loading ? "…" : <><Search className="inline w-4 h-4 mr-1" /> Chercher</>}
        </FlowerButton>
      </form>

      {state.error === "notfound" && (
        <p className="mt-6 rounded-3xl bg-[#FADADD]/60 border-2 border-dashed border-[#D1D5CB] p-5 text-[#78353A]" data-testid="tracking-notfound">
          Hum… Finn a cherché partout mais ne trouve pas cette commande. Vérifie le numéro (format MM-XXXXXXXX) ou réponds à ton e-mail de confirmation, on t'aidera avec plaisir ✿
        </p>
      )}
      {state.error === "error" && (
        <p className="mt-6 rounded-3xl bg-[#FADADD]/60 border-2 border-dashed border-[#D1D5CB] p-5 text-[#78353A]" data-testid="tracking-error">
          Petit souci technique, réessaie dans un instant.
        </p>
      )}
      {state.order && <OrderResult order={state.order} />}

      <p className="mt-10 text-sm text-[#4A3B32]/70">
        Pas encore de commande ? <Link to="/boutique" className="text-[#78353A] underline decoration-dotted underline-offset-4">Découvrir la boutique</Link>
      </p>
    </div>
  );
}
